import React, {useContext, useEffect} from 'react';
import FormField, {Props} from '@src/components/Form/FormField';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import {NestedListWidgetProps} from '@src/@types/components/Form';
import {FormTabsContext} from '@src/contexts/FormTabsContext';
import ControlledList from '@src/components/ControlledList/ControlledList';
import {ListConfigFieldTypes} from '@src/@types/components/List';
import {ControlledListProps} from '@src/@types/components/ControlledList';
import {MessageColumn} from './MessageColumn';
import {AttentionMessageProps} from './messages';
import {CreatorColumn} from './CreatorColumn';
import {SolverColumn} from './SolverColumn';
import {useQuery} from '@tanstack/react-query';
import app from '@src/helpers/app';

const columns = [
    {
        name: 'id',
        label: '#',
        type: ListConfigFieldTypes.NUMBER,
        invisible: true,
    },
    {
        name: 'message',
        label: 'OrderForm.attentions.fields.message',
        type: ListConfigFieldTypes.CUSTOM,
    },
    {
        name: 'solver',
        label: 'OrderForm.attentions.fields.solver',
        type: ListConfigFieldTypes.RELATION,
        relation: 'solver',
        nameFrom: 'name'
    },
    {
        name: 'solved_at',
        label: 'OrderForm.attentions.fields.solved_at',
        type: ListConfigFieldTypes.TIME_SINCE,
    },
    {
        name: 'creator',
        label: 'OrderForm.attentions.fields.creator',
        type: ListConfigFieldTypes.RELATION,
        relation: 'creator',
        nameFrom: 'name',
        invisible: true,
    },
    {
        name: 'created_at',
        label: 'OrderForm.attentions.fields.created_at',
        type: ListConfigFieldTypes.TIME_SINCE,
    },
];

const listContextValue: ControlledListProps<AttentionMessageProps['model']>['listContextValues'] = {
    getCustomColumnComponent(config) {
        if (config.name === 'message') {
            return MessageColumn;
        } else if (config.name === 'creator') {
            return CreatorColumn;
        } else if (config.name === 'solver') {
            return SolverColumn;
        }

        return null;
    }
};

function ActualField<Model extends IOrderModel>(props: NestedListWidgetProps<Model>) {
    const tabsContext = useContext(FormTabsContext);
    const queryKeys = app('queryKeys').getCustomQueryOfExactModelQueryKey(
            'Orders',
            props.model?.id,
            'attentions-list'
    );
    const queryResult = useQuery<IOrderModel['attentions'], Error>(
            queryKeys,
            () => {
                return app('http').route('orders.attentions.index', {
                    id: String(props.model?.id)
                }, {
                    body: {
                        ['controller-name']: props.formName
                    }
                });
            },
            {enabled: Boolean(props.model?.id)}
    );
    const attentions = (queryResult.data ?? []).sort((a, b) => b.id - a.id);
    const numberOfSolved = attentions.filter(item => Boolean(item.solved_at)).length;
    const {tabName} = props;

    useEffect(() => {
        if (!tabName || !tabsContext || !tabsContext.isVisibleTab(tabName)) {
            return;
        }

        const hasUnsolved = numberOfSolved < attentions.length;

        tabsContext.toggleExclamationSign(tabName, hasUnsolved);
        tabsContext.setBadge(tabName, {
            label: hasUnsolved ? `${numberOfSolved} / ${attentions.length}` : String(attentions.length),
            color: hasUnsolved ? 'danger' : (attentions.length ? 'success' : 'primary'),
        });
    }, [attentions.length, tabName, numberOfSolved, tabsContext]);

    return <ControlledList<NonNullable<IOrderModel['attentions']>[number]>
            queryResult={queryResult}
            uniqueListName={`${props.formName}@${props.fieldName}--nested-list-widget`}
            controllerName={props.formName}
            records={attentions}
            columns={columns}
            withoutSearchField={true}
            keepInParentBox={true}
            bordered={true}
            listContextValues={listContextValue}
            listContentProps={{
                tableProps: {className: 'models-list__content-table--valign-top'}
            }}
    />;
}

export default function OrderAttentionsField<Model extends IOrderModel = IOrderModel>(props: Props<Model>) {
    return <FormField
            {...props}
            withoutLayout={true}
            fieldElement={ActualField}
    />;
}
